import { amplifyProxy } from '@/services/directApi'

export default {
  namespaced: true,
  state: {
    unreconciledPaymentsRecords: { Data: [], Count: 0, TotalAmount: 0 },
    paymentGroupsRecords: { Data: [], Count: 0, TotalAmount: 0 },
    reconciledStatementsRecords: { Data: [], Count: 0, TotalAmount: 0 },
    totalCount: {
      unreconciled: 0,
      paymentGroups: 0,
      reconciledStatements: 0
    },
    totalAmount: {
      unreconciled: 0,
      paymentGroups: 0,
      reconciledStatements: 0
    },
    pagination: {
      unreconciled: { offset: 0, limit: 10 },
      paymentGroups: { offset: 0, limit: 10 },
      reconciledStatements: { offset: 0, limit: 10 }
    },
    loading: {
      unreconciled: false,
      paymentGroups: false,
      reconciledStatements: false
    },
    error: {
      unreconciled: null,
      paymentGroups: null,
      reconciledStatements: null
    },
    pageCache: {}
  },
  mutations: {
    SET_UNRECONCILED_PAYMENTS_RECORDS(state, data) {
      state.unreconciledPaymentsRecords = data
    },
    SET_PAYMENT_GROUPS_RECORDS(state, data) {
      state.paymentGroupsRecords = data
    },
    SET_RECONCILED_STATEMENTS_RECORDS(state, data) {
      state.reconciledStatementsRecords = data
    },
    SET_TOTAL_COUNT(state, { type, count }) {
      state.totalCount[type] = count
    },
    SET_TOTAL_AMOUNT(state, { type, amount }) {
      state.totalAmount[type] = amount
    },
    SET_PAGINATION(state, { type, offset, limit }) {
      state.pagination[type] = { offset, limit }
    },
    SET_LOADING(state, { type, value }) {
      state.loading[type] = value
    },
    SET_ERROR(state, { type, error }) {
      state.error[type] = error
    },
    SET_PAGE_CACHE(state, { key, data }) {
      state.pageCache[key] = {
        data,
        timestamp: Date.now()
      }
    },
    CLEAR_PAGE_CACHE(state) {
      state.pageCache = {}
    }
  },
  actions: {
    async getUnreconciledPaymentsRecords({ commit, rootState, state }, { offset = 0, limit = 10, forceRefresh = false, fromDate = '', toDate = '' } = {}) {
      const cacheKey = `unreconciled_${rootState.branchID}_${offset}_${limit}_${fromDate}_${toDate}`
      
      // Check cache
      if (!forceRefresh && state.pageCache[cacheKey]) {
        const cached = state.pageCache[cacheKey]
        const cacheAge = Date.now() - cached.timestamp
        const cacheTimeout = 5 * 60 * 1000 // 5 minutes
        
        if (cacheAge < cacheTimeout) {
          const { data, count, totalAmount } = cached.data
          commit('SET_UNRECONCILED_PAYMENTS_RECORDS', { Data: data, Count: count, TotalAmount: totalAmount })
          commit('SET_TOTAL_COUNT', { type: 'unreconciled', count })
          commit('SET_TOTAL_AMOUNT', { type: 'unreconciled', amount: totalAmount })
          commit('SET_PAGINATION', { type: 'unreconciled', offset, limit })
          return { data, count, totalAmount }
        }
      }
      
      commit('SET_LOADING', { type: 'unreconciled', value: true })
      commit('SET_ERROR', { type: 'unreconciled', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/payments/unreconciled/records/${offset}/${limit}`
        const queryParams = {
          branchID: rootState.branchID
        }

        // Add optional date parameters if provided
        if (fromDate) queryParams.fromDate = fromDate
        if (toDate) queryParams.toDate = toDate

        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: queryParams
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        const totalAmount = response.data?.TotalAmount || data.reduce((sum, item) => sum + (item.Amount || 0), 0)
        
        // Store as object with Data and Count properties to match component expectations
        commit('SET_UNRECONCILED_PAYMENTS_RECORDS', { Data: data, Count: count, TotalAmount: totalAmount })
        commit('SET_TOTAL_COUNT', { type: 'unreconciled', count })
        commit('SET_TOTAL_AMOUNT', { type: 'unreconciled', amount: totalAmount })
        commit('SET_PAGINATION', { type: 'unreconciled', offset, limit })
        
        // Cache the result
        commit('SET_PAGE_CACHE', {
          key: cacheKey,
          data: { data, count, totalAmount }
        })
        
        return { data, count, totalAmount }
      } catch (error) {
        commit('SET_ERROR', { type: 'unreconciled', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'unreconciled', value: false })
      }
    },

    async getPaymentGroupsRecords({ commit, rootState, state }, { offset = 0, limit = 10, forceRefresh = false, state: filterState = '', fromDate = '', toDate = '' } = {}) {
      const cacheKey = `paymentGroups_${rootState.branchID}_${offset}_${limit}_${filterState}_${fromDate}_${toDate}`
      
      // Check cache
      if (!forceRefresh && state.pageCache[cacheKey]) {
        const cached = state.pageCache[cacheKey]
        const cacheAge = Date.now() - cached.timestamp
        const cacheTimeout = 5 * 60 * 1000 // 5 minutes
        
        if (cacheAge < cacheTimeout) {
          const { data, count, totalAmount } = cached.data
          commit('SET_PAYMENT_GROUPS_RECORDS', { Data: data, Count: count, TotalAmount: totalAmount })
          commit('SET_TOTAL_COUNT', { type: 'paymentGroups', count })
          commit('SET_TOTAL_AMOUNT', { type: 'paymentGroups', amount: totalAmount })
          commit('SET_PAGINATION', { type: 'paymentGroups', offset, limit })
          return { data, count, totalAmount }
        }
      }
      
      commit('SET_LOADING', { type: 'paymentGroups', value: true })
      commit('SET_ERROR', { type: 'paymentGroups', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/paymentgroups/records/${offset}/${limit}`
        const queryParams = {
          branchID: rootState.branchID
        }

        // Add optional filter parameters if provided
        if (filterState) queryParams.state = filterState
        if (fromDate) queryParams.fromDate = fromDate
        if (toDate) queryParams.toDate = toDate

        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: queryParams
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.data?.Data || response.data || []
        const count = response.data?.Count || data.length
        const totalAmount = response.data?.TotalAmount || data.reduce((sum, item) => sum + (item.Amount || 0), 0)
        
        // Store as object with Data and Count properties to match component expectations
        commit('SET_PAYMENT_GROUPS_RECORDS', { Data: data, Count: count, TotalAmount: totalAmount })
        commit('SET_TOTAL_COUNT', { type: 'paymentGroups', count })
        commit('SET_TOTAL_AMOUNT', { type: 'paymentGroups', amount: totalAmount })
        commit('SET_PAGINATION', { type: 'paymentGroups', offset, limit })
        
        // Cache the result
        commit('SET_PAGE_CACHE', {
          key: cacheKey,
          data: { data, count, totalAmount }
        })
        
        return { data, count, totalAmount }
      } catch (error) {
        commit('SET_ERROR', { type: 'paymentGroups', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'paymentGroups', value: false })
      }
    },

    async getReconciledStatementsRecords({ commit, rootState, state }, { offset = 0, limit = 10, forceRefresh = false, bankAccountID = '', fromDate = '', toDate = '', personId = '' } = {}) {
      const cacheKey = `reconciledStatements_${rootState.branchID}_${offset}_${limit}_${bankAccountID}_${fromDate}_${toDate}_${personId}`
      
      // Check cache
      if (!forceRefresh && state.pageCache[cacheKey]) {
        const cached = state.pageCache[cacheKey]
        const cacheAge = Date.now() - cached.timestamp
        const cacheTimeout = 5 * 60 * 1000 // 5 minutes
        
        if (cacheAge < cacheTimeout) {
          const { data, count, totalAmount } = cached.data
          commit('SET_RECONCILED_STATEMENTS_RECORDS', { Data: data, Count: count, TotalAmount: totalAmount })
          commit('SET_TOTAL_COUNT', { type: 'reconciledStatements', count })
          commit('SET_TOTAL_AMOUNT', { type: 'reconciledStatements', amount: totalAmount })
          commit('SET_PAGINATION', { type: 'reconciledStatements', offset, limit })
          return { data, count, totalAmount }
        }
      }
      
      commit('SET_LOADING', { type: 'reconciledStatements', value: true })
      commit('SET_ERROR', { type: 'reconciledStatements', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/statements/reconciled/${offset}/${limit}`
        const queryParams = {
          branchID: rootState.branchID
        }

        // Add optional parameters if provided
        if (bankAccountID) queryParams.bankAccountID = bankAccountID
        if (fromDate) queryParams.fromDate = fromDate
        if (toDate) queryParams.toDate = toDate
        if (personId) queryParams.personId = personId

        const options = {
          headers: {
            token: rootState.user.LoginToken,
          },
          queryStringParameters: queryParams
        }

        const response = await amplifyProxy.get(endpoint, options)
        const data = response.Results || response.data?.Results || []
        const count = response.Count || response.data?.Count || data.length
        const totalAmount = data.reduce((sum, item) => sum + (item.Amount || 0), 0)
        
        // Store as object with Data and Count properties to match component expectations
        commit('SET_RECONCILED_STATEMENTS_RECORDS', { Data: data, Count: count, TotalAmount: totalAmount })
        commit('SET_TOTAL_COUNT', { type: 'reconciledStatements', count })
        commit('SET_TOTAL_AMOUNT', { type: 'reconciledStatements', amount: totalAmount })
        commit('SET_PAGINATION', { type: 'reconciledStatements', offset, limit })
        
        // Cache the result
        commit('SET_PAGE_CACHE', {
          key: cacheKey,
          data: { data, count, totalAmount }
        })
        
        return { data, count, totalAmount }
      } catch (error) {
        commit('SET_ERROR', { type: 'reconciledStatements', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'reconciledStatements', value: false })
      }
    }
  }
}